import { useState, useEffect, useRef } from 'react'
import { 
  User, Mail, Phone, Building2, Briefcase, Camera, 
  Save, Edit, X, Check, MapPin, Calendar, Shield, Upload,
  Smartphone, Key, Loader2, Copy, Eye, EyeOff, Lock
} from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import api from '../lib/api'
import toast from 'react-hot-toast'

const Profile = () => {
  const { user, setUser } = useAuthStore()
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('profile')
  const fileInputRef = useRef(null)
  
  // 2FA State
  const [twoFactorStatus, setTwoFactorStatus] = useState({ enabled: false, backupCodesRemaining: 0 })
  const [showSetup2FA, setShowSetup2FA] = useState(false)
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [verifyCode, setVerifyCode] = useState('')
  const [backupCodes, setBackupCodes] = useState([])
  const [showBackupCodes, setShowBackupCodes] = useState(false)
  const [disablePassword, setDisablePassword] = useState('')
  const [disableCode, setDisableCode] = useState('')
  const [showDisable2FA, setShowDisable2FA] = useState(false)
  const [twoFactorLoading, setTwoFactorLoading] = useState(false)
  
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    department: '',
    title: '',
    role: '',
    avatar: '',
    bio: '',
    location: '',
    skills: []
  })

  const [newSkill, setNewSkill] = useState('')

  // Fetch fresh user data on mount
  useEffect(() => {
    fetchUserProfile()
    fetch2FAStatus()
  }, [])

  const fetch2FAStatus = async () => {
    try {
      const response = await api.get('/auth/2fa/status')
      setTwoFactorStatus(response.data)
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error)
    }
  }

  const handleSetup2FA = async () => {
    setTwoFactorLoading(true)
    try {
      const response = await api.post('/auth/2fa/setup')
      setQrCode(response.data.qrCode)
      setSecret(response.data.secret)
      setShowSetup2FA(true)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to setup 2FA')
    } finally {
      setTwoFactorLoading(false)
    }
  }

  const handleVerify2FA = async () => {
    if (verifyCode.length < 6) {
      toast.error('Please enter a 6-digit code')
      return
    }
    setTwoFactorLoading(true)
    try {
      const response = await api.post('/auth/2fa/verify', { code: verifyCode })
      setBackupCodes(response.data.backupCodes)
      setShowBackupCodes(true)
      setShowSetup2FA(false)
      setTwoFactorStatus({ enabled: true, backupCodesRemaining: 10 })
      toast.success('2FA enabled successfully!')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Invalid verification code')
    } finally {
      setTwoFactorLoading(false)
    }
  }

  const handleDisable2FA = async () => {
    if (!disablePassword || disableCode.length < 6) {
      toast.error('Please enter your password and 2FA code')
      return
    }
    setTwoFactorLoading(true)
    try {
      await api.post('/auth/2fa/disable', { password: disablePassword, code: disableCode })
      setTwoFactorStatus({ enabled: false, backupCodesRemaining: 0 })
      setShowDisable2FA(false)
      setDisablePassword('')
      setDisableCode('')
      toast.success('2FA has been disabled')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to disable 2FA')
    } finally {
      setTwoFactorLoading(false)
    }
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }

  const fetchUserProfile = async () => {
    try {
      setLoading(true)
      const response = await api.get('/auth/me')
      const userData = response.data
      setProfile({
        name: userData.name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        department: userData.department || 'BD',
        title: userData.title || '',
        role: userData.role || 'BD Executive',
        avatar: userData.avatar || '',
        bio: userData.bio || '',
        location: userData.location || '',
        skills: userData.skills || []
      })
      // Update auth store
      if (setUser) {
        setUser(userData)
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error)
      // Fallback to stored user data
      if (user) {
        setProfile({
          name: user.name || '',
          email: user.email || '',
          phone: user.phone || '',
          department: user.department || 'BD',
          title: user.title || '',
          role: user.role || 'BD Executive',
          avatar: user.avatar || '',
          bio: user.bio || '',
          location: user.location || '',
          skills: user.skills || []
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }))
  }

  const handleAddSkill = () => {
    if (newSkill.trim() && !profile.skills.includes(newSkill.trim())) {
      setProfile(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }))
      setNewSkill('')
    }
  }

  const handleRemoveSkill = (skill) => {
    setProfile(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skill)
    }))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const response = await api.put('/auth/profile', {
        name: profile.name,
        phone: profile.phone,
        department: profile.department,
        title: profile.title,
        bio: profile.bio,
        location: profile.location,
        skills: profile.skills,
        avatar: profile.avatar
      })
      // Update auth store with new data
      if (setUser) {
        setUser(response.data)
      }
      toast.success('Profile updated successfully')
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to update profile:', error)
      toast.error(error.response?.data?.error || 'Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB')
      return
    }

    // Convert to base64 for storage
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64String = reader.result
      setProfile(prev => ({ ...prev, avatar: base64String }))
      toast.success('Avatar updated! Click Save to apply changes.')
    }
    reader.onerror = () => {
      toast.error('Failed to read image file')
    }
    reader.readAsDataURL(file)
  }

  const departments = ['Tech', 'Growth', 'Management', 'BD', 'Sales', 'Marketing', 'Operations', 'Finance', 'HR', 'Leadership']
  const roles = ['Admin', 'BD Manager', 'BD Executive', 'Viewer']

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <User className="w-7 h-7" />
          My Profile
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Manage your personal information and preferences
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setActiveTab('profile')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'profile'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          <User className="w-4 h-4 inline mr-2" />
          Profile
        </button>
        <button
          onClick={() => setActiveTab('security')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'security'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          <Shield className="w-4 h-4 inline mr-2" />
          Security
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : activeTab === 'security' ? (
        /* Security Tab */
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Security Settings
          </h2>

          {/* Two-Factor Authentication Section */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                  <Smartphone className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">Two-Factor Authentication</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Add an extra layer of security to your account using an authenticator app
                  </p>
                  {twoFactorStatus.enabled && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-medium">
                        <Check className="w-3 h-3 mr-1" />
                        Enabled
                      </span>
                      <span className="text-xs text-gray-500">
                        {twoFactorStatus.backupCodesRemaining} backup codes remaining
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div>
                {!twoFactorStatus.enabled ? (
                  <button
                    onClick={handleSetup2FA}
                    disabled={twoFactorLoading}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {twoFactorLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                    Enable 2FA
                  </button>
                ) : (
                  <button
                    onClick={() => setShowDisable2FA(true)}
                    className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                  >
                    Disable 2FA
                  </button>
                )}
              </div>
            </div>

            {/* 2FA Setup Modal */}
            {showSetup2FA && (
              <div className="mt-6 p-6 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <h4 className="font-medium text-gray-900 dark:text-white mb-4">Setup Two-Factor Authentication</h4>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      1. Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                    </p>
                    {qrCode && (
                      <div className="bg-white p-4 rounded-lg inline-block">
                        <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-3">
                      Can't scan? Enter this code manually:
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="text-xs bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded font-mono">
                        {secret}
                      </code>
                      <button onClick={() => copyToClipboard(secret)} className="text-gray-500 hover:text-gray-700">
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      2. Enter the 6-digit code from your authenticator app to verify
                    </p>
                    <input
                      type="text"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="w-full px-4 py-3 text-center text-2xl tracking-widest border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      maxLength={6}
                    />
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={handleVerify2FA}
                        disabled={twoFactorLoading || verifyCode.length < 6}
                        className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {twoFactorLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        Verify & Enable
                      </button>
                      <button
                        onClick={() => { setShowSetup2FA(false); setVerifyCode(''); }}
                        className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Backup Codes Display */}
            {showBackupCodes && backupCodes.length > 0 && (
              <div className="mt-6 p-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Save Your Backup Codes
                </h4>
                <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
                  Store these codes in a safe place. You can use them to access your account if you lose your authenticator device.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
                  {backupCodes.map((code, index) => (
                    <code key={index} className="text-center py-2 bg-white dark:bg-gray-800 rounded font-mono text-sm">
                      {code}
                    </code>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => copyToClipboard(backupCodes.join('\n'))}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Copy All Codes
                  </button>
                  <button
                    onClick={() => setShowBackupCodes(false)}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg"
                  >
                    I've Saved These
                  </button>
                </div>
              </div>
            )}

            {/* Disable 2FA Modal */}
            {showDisable2FA && (
              <div className="mt-6 p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                <h4 className="font-medium text-red-800 dark:text-red-200 mb-4">Disable Two-Factor Authentication</h4>
                <p className="text-sm text-red-700 dark:text-red-300 mb-4">
                  To disable 2FA, please enter your password and a code from your authenticator app.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
                    <input
                      type="password"
                      value={disablePassword}
                      onChange={(e) => setDisablePassword(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Enter your password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">2FA Code</label>
                    <input
                      type="text"
                      value={disableCode}
                      onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="000000"
                      maxLength={6}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleDisable2FA}
                      disabled={twoFactorLoading}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {twoFactorLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Disable 2FA
                    </button>
                    <button
                      onClick={() => { setShowDisable2FA(false); setDisablePassword(''); setDisableCode(''); }}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Cover & Avatar */}
        <div className="relative h-32 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500">
          <div className="absolute -bottom-12 left-6">
            <div className="relative">
              {profile.avatar ? (
                <img 
                  src={profile.avatar} 
                  alt={profile.name}
                  className="w-24 h-24 rounded-2xl object-cover border-4 border-white dark:border-gray-800 shadow-xl"
                />
              ) : (
                <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-3xl font-bold text-white border-4 border-white dark:border-gray-800 shadow-xl">
                  {profile.name?.charAt(0) || 'U'}
                </div>
              )}
              {isEditing && (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleAvatarUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-0 right-0 p-1.5 bg-blue-500 rounded-lg text-white hover:bg-blue-600 transition-colors"
                  >
                    <Camera className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
          
          {/* Edit Button */}
          <div className="absolute top-4 right-4">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 transition-colors"
              >
                <Edit className="w-4 h-4" />
                Edit Profile
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 transition-colors"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-gray-900 rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Profile Info */}
        <div className="pt-16 px-6 pb-6">
          {/* Name & Role Badge */}
          <div className="flex items-center gap-3 mb-6">
            {isEditing ? (
              <input
                type="text"
                value={profile.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="text-2xl font-bold bg-transparent border-b-2 border-blue-500 text-gray-900 dark:text-white focus:outline-none"
                placeholder="Your Name"
              />
            ) : (
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{profile.name}</h2>
            )}
            <span className="px-3 py-1 bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 text-sm font-medium rounded-full flex items-center gap-1">
              <Shield className="w-3 h-3" />
              {profile.role}
            </span>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Email */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                <Mail className="w-4 h-4" />
                Email
              </label>
              <input
                type="email"
                value={profile.email}
                onChange={(e) => handleChange('email', e.target.value)}
                disabled={!isEditing}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.05] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white disabled:opacity-60"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                <Phone className="w-4 h-4" />
                Phone
              </label>
              <input
                type="tel"
                value={profile.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                disabled={!isEditing}
                placeholder="+91 98765 43210"
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.05] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white disabled:opacity-60"
              />
            </div>

            {/* Department */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                <Building2 className="w-4 h-4" />
                Department
              </label>
              {isEditing ? (
                <select
                  value={profile.department}
                  onChange={(e) => handleChange('department', e.target.value)}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.05] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white"
                >
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={profile.department}
                  disabled
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.05] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white disabled:opacity-60"
                />
              )}
            </div>

            {/* Title */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                <Briefcase className="w-4 h-4" />
                Job Title
              </label>
              <input
                type="text"
                value={profile.title}
                onChange={(e) => handleChange('title', e.target.value)}
                disabled={!isEditing}
                placeholder="e.g., Senior BD Manager"
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.05] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white disabled:opacity-60"
              />
            </div>

            {/* Location */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                <MapPin className="w-4 h-4" />
                Location
              </label>
              <input
                type="text"
                value={profile.location}
                onChange={(e) => handleChange('location', e.target.value)}
                disabled={!isEditing}
                placeholder="e.g., Mumbai, India"
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.05] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white disabled:opacity-60"
              />
            </div>

            {/* Role (Admin only can change) */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                <Shield className="w-4 h-4" />
                Role
              </label>
              <input
                type="text"
                value={profile.role}
                disabled
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.05] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white disabled:opacity-60"
              />
            </div>
          </div>

          {/* Bio */}
          <div className="mt-6 space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400">
              Bio
            </label>
            <textarea
              value={profile.bio}
              onChange={(e) => handleChange('bio', e.target.value)}
              disabled={!isEditing}
              rows={3}
              placeholder="Tell us about yourself..."
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.05] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white disabled:opacity-60 resize-none"
            />
          </div>

          {/* Skills */}
          <div className="mt-6 space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400">
              Skills
            </label>
            <div className="flex flex-wrap gap-2">
              {profile.skills.map((skill, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 dark:bg-white/[0.05] text-gray-700 dark:text-gray-300 rounded-lg text-sm"
                >
                  {skill}
                  {isEditing && (
                    <button
                      onClick={() => handleRemoveSkill(skill)}
                      className="ml-1 text-gray-400 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </span>
              ))}
              {isEditing && (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddSkill()}
                    placeholder="Add skill..."
                    className="px-3 py-1.5 bg-gray-50 dark:bg-white/[0.05] border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white w-32"
                  />
                  <button
                    onClick={handleAddSkill}
                    className="p-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Member Since */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Calendar className="w-4 h-4" />
              Member since {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'N/A'}
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  )
}

export default Profile
